import { useEffect, useRef, useState } from 'react';
import { api, type Cust, type CustContact, type RecvInput, type Staff } from '../lib/api';
import { loadStaff } from '../lib/staff';
import { useAutoSearch } from '../lib/search';
import { canScreen, useMe } from '../lib/me';
import Modal from './Modal';
import MasterForm from '../pages/MasterForm';

/**
 * 접수 헤더 입력칸 — 접수 수정(`RecvForm`)과 접수+노선 동시 등록(`RecvIntakeForm`)이 함께 쓴다.
 *
 * 두 화면에 같은 칸을 두 벌 두면 한쪽에만 필드가 늘어난다. 칸은 여기 한 곳에만 있다.
 *
 * 레거시(`usp_Recv_Create`)를 따르는 것
 * - **접수일은 입력받지 않는다.** SP 가 `Set @RecvDate = @CreateDate` 로 파라미터를 버리고
 *   등록일로 덮어쓴다. 입력칸을 두면 "입력했는데 다른 날짜로 저장된다"가 된다.
 *
 * 일부러 다르게 한 것
 * - 입찰로 등록하면 **마감일이 필수**다. 레거시는 비워둘 수 있는데 그러면 노선 상태 계산이
 *   전부 NULL 이 되어 화면에 '—'로 뜬다. 입력 시점에 막는다.
 */

/**
 * 화면 전용 칸(`custName`·`finReqLocked`·`billedMons`)은 서버로 보내지 않는다 — 저장 직전 `toRecvInput` 으로 걷는다.
 * `finReqLocked` = 청구 요청(청구대상 이상)한 달이 있어 이 사람은 청구 방법을 못 바꾼다(#180 · 서버 `getRecv` 가 계산).
 */
export type RecvDraft = RecvInput & {
  custName?: string; finReqLocked?: boolean; billedMons?: string[];
  /** 함께 담당 이름(id → 이름) — 비활성이 돼 담당자 목록에 없는 사람도 칩에 이름이 보이게(getRecv co_owners). */
  coOwnerLabels?: Record<string, string>;
  /** 수정 창 전용 — 웹 지정 담당이 없을 때 실제로 보는 담당(레거시 연결) 이름. 비워 저장하면 그대로 남는다(서버 coalesce). */
  legacyOwnerLabel?: string;
};

/** 저장할 때 화면 전용 칸을 뺀다(두 화면 공통). */
export function toRecvInput(d: RecvDraft): RecvInput {
  const { custName, finReqLocked, billedMons, coOwnerLabels, legacyOwnerLabel, ...rest } = d;
  void custName;
  void finReqLocked;
  void billedMons;
  void coOwnerLabels;
  void legacyOwnerLabel;
  return rest;
}

const REQ_METHODS = ['세금계산서', '현금', '카드', '기타'];
export const BIDDING_TYPES = ['조건차량 즉시배차', '모든차량 즉시배차']; // 심사배차 없앰(10-23 way — 입찰 신청 = 바로 배정)

export function RecvFields({
  d,
  set,
}: {
  d: RecvDraft;
  set: (patch: Partial<RecvDraft>) => void;
}) {
  // 거래처 담당자(#177) — 거래처를 고르면 그 거래처에 등록된 담당자로 칸을 채운다.
  // 사람이 고친 칸은 덮지 않는다: 비어 있거나 **직전에 자동으로 채운 값 그대로**일 때만 바꾼다.
  const [contacts, setContacts] = useState<CustContact[]>([]);
  const auto = useRef<Partial<RecvDraft>>({});
  const custId = d.custId;
  // 거래처 대표 전화(#365) — 고를 때 받은 값 · 수정 창처럼 처음부터 거래처가 있으면 번호로 한 번 찾는다.
  // 레거시 전화 칸엔 이메일이 섞여 있다(issues 250 · 서버 `dispatch.ts PHONE_OK` 와 같은 기준) — 전화로 보일 때만 꺼낸다.
  const telOf = (v: string | null | undefined) => {
    const s = (v ?? '').trim();
    return !s.includes('@') && s.replace(/[^0-9]/g, '').length >= 8 ? s : '';
  };
  const [custTel, setCustTel] = useState<{ id: number; phone: string } | null>(null);
  const telRef = useRef<number | null>(null);
  useEffect(() => {
    if (!custId) { telRef.current = null; setCustTel(null); return; }
    if (telRef.current === custId) return;
    telRef.current = custId;
    let alive = true;
    api.listCusts({ q: String(custId), limit: 50 })
      .then((r) => { const c = r.rows.find((x) => x.cust_id === custId); if (alive) setCustTel({ id: custId, phone: telOf(c?.phone) }); })
      .catch(() => { /* 전화번호를 못 불러와도 접수는 쓴다 */ });
    return () => { alive = false; };
  }, [custId]);
  useEffect(() => {
    if (!custId) { setContacts([]); return; }
    let alive = true;
    api.listCustContacts({ custId }).then((r) => { if (alive) setContacts(r.rows); }).catch(() => { if (alive) setContacts([]); });
    return () => { alive = false; };
  }, [custId]);

  async function pickCust(c: Cust) {
    telRef.current = c.cust_id;
    setCustTel({ id: c.cust_id, phone: telOf(c.phone) });
    set({ custId: c.cust_id, custName: c.cust_name ?? '' });
    let rows: CustContact[] = [];
    try { rows = (await api.listCustContacts({ custId: c.cust_id })).rows; } catch { /* 담당자를 못 불러와도 거래처는 고른다 */ }
    const gen = rows.find((x) => x.kind === '일반담당자');
    const fin = rows.find((x) => x.kind === '정산담당자');
    const next: Partial<RecvDraft> = {
      recvUser: gen?.name ?? '',
      recvUserEmail: gen?.email ?? '',
      recvUserPhone: telOf(gen?.phone),
      finManager: fin?.name ?? '',
      finPhone: fin?.phone ?? '',
    };
    const patch: Partial<RecvDraft> = {};
    for (const k of Object.keys(next) as (keyof typeof next)[]) {
      const cur = (d[k] ?? '') as string;
      if (cur === '' || cur === (auto.current[k] ?? '')) (patch as Record<string, unknown>)[k] = next[k];
    }
    auto.current = next;
    set(patch);
  }

  // 거래처 담당은 등록된 담당자 **전부**에서 고른다(일반 먼저) — 레거시 담당자 줄은 73% 가 「정산담당자」로만 들어가 있어
  // 일반담당자만 보이면 대부분 거래처에서 고를 사람이 없다(국민대 1306 = 정산담당자 2명뿐).
  const genContacts = [
    ...contacts.filter((x) => x.kind === '일반담당자'),
    ...contacts.filter((x) => x.kind !== '일반담당자'),
  ].filter((x) => x.name || x.email);
  const finContacts = contacts.filter((x) => x.kind === '정산담당자' && (x.name || x.phone));

  // 칸에 친 새 사람은 **접수를 저장하면** 거래처 담당자 목록 끝에 자동으로 들어간다(10-30 way — 10-14 등록 버튼을 없앰 ·
  // 서버 `autoAddCustContactsTx`). 여기선 그렇게 된다는 안내만. 판정은 서버와 같은 모양(이름+이메일 / 정산은 이름+전화).
  const same = (a: string | undefined, b: string | undefined) => (a ?? '').trim().toLowerCase() === (b ?? '').trim().toLowerCase();
  const digitsOf = (s: string | undefined) => (s ?? '').replace(/[^0-9]/g, '');
  const genName = (d.recvUser ?? '').trim();
  const finName = (d.finManager ?? '').trim();
  const genNew = !!custId && !!genName && !contacts.some((x) => same(x.name, genName) && same(x.email, d.recvUserEmail));
  const finNew = !!custId && !!finName && !contacts.some((x) => x.kind === '정산담당자' && same(x.name, finName) && digitsOf(x.phone) === digitsOf(d.finPhone));

  return (
    <div className="grid gap-4 md:grid-cols-2">
      <Field label="접수 구분" required hint="통근도 같은 화면에서 등록합니다">
        <select className="field" value={d.recvType ?? '일반'} onChange={(e) => set({ recvType: e.target.value })}>
          <option value="일반">일반</option>
          <option value="통근">통근</option>
        </select>
      </Field>

      <Field label="거래처" required>
        <CustPicker
          value={d.custId}
          label={d.custName}
          onPick={(c) => { set({ newCust: undefined }); void pickCust(c); }}
          onTerm={d.recvId ? undefined : (t) => set({ newCust: { ...d.newCust, custName: t } })}
        />
        {!d.recvId && !custId && (
          <NewCustBiz
            name={d.newCust?.custName ?? ''}
            biz={d.newCust?.bizNumber ?? ''}
            onBiz={(v) => set({ newCust: { ...d.newCust, bizNumber: v } })}
            onPick={(c) => { set({ newCust: undefined }); void pickCust(c); }}
          />
        )}
        {custId && custTel?.id === custId && (
          <CustTelLine custId={custId} phone={custTel.phone} onSaved={(p) => setCustTel({ id: custId, phone: p })} />
        )}
      </Field>

      {/* ⚠️ '거래처 담당'과 '버스인접수담당자'는 다른 축이다.
          거래처 담당 = 거래처 쪽 사람(이름 = 레거시 `recv_user` · 이메일 = `recv_user_email`, #177 에서 「접수자」를 바꿈)
          버스인접수담당자 = 우리 쪽 계정(10-14 way 「담당자」에서 바꿈). **누가 이 접수를 보는지**를 이게 정한다(`owner_app_user_id`).
          라벨이 비슷해 나란히 두고 각각 무엇인지 아래에 한 줄로 적는다. */}
      <div className="block">
        {/* 반칸 폭이라 라벨·안내·고르기를 한 줄에 두면 라벨이 한 글자씩 꺾인다(10-21 way 캡처) — 안내는 아래 줄로. */}
        <span className="flex items-center gap-2 text-sm font-medium">
          <span className="whitespace-nowrap">
            거래처담당자
            {/* 새 접수는 이름·이메일·전화 필수(#388) — 수정 창은 옛 접수(이름만)를 고칠 수 있게 안 건다(서버 requireRecvUserContact). */}
            {!d.recvId && <span className="ml-0.5 text-red-600">*</span>}
          </span>
          {genContacts.length > 0 && (
            <ContactChooser
              rows={genContacts}
              label={(x) => [x.kind === '정산담당자' ? '[정산]' : '', x.dept ? `(${x.dept})` : '', x.name, x.phone, x.email].filter(Boolean).join(' ')}
              onPick={(x) => set({ recvUser: x.name, recvUserEmail: x.email, recvUserPhone: telOf(x.phone) })}
            />
          )}
        </span>
        <span className="mb-1 block text-xs text-ink-faint">
          등록된 거래처담당자에서 고르거나, 새로 쳐서 등록합니다{!d.recvId && ' · 이름·이메일·전화번호 모두 필수'}
        </span>
        <div className="grid grid-cols-[minmax(0,2fr)_minmax(0,3fr)_minmax(0,2fr)] gap-2">
          <input
            className="field"
            aria-label="거래처담당자 이름"
            placeholder="이름"
            value={d.recvUser ?? ''}
            onChange={(e) => set({ recvUser: e.target.value })}
          />
          <input
            className="field"
            type="email"
            aria-label="거래처담당자 이메일"
            placeholder="이메일"
            value={d.recvUserEmail ?? ''}
            onChange={(e) => set({ recvUserEmail: e.target.value })}
          />
          <input
            className="field"
            type="tel"
            inputMode="tel"
            aria-label="거래처담당자 전화번호"
            placeholder="전화번호"
            value={d.recvUserPhone ?? ''}
            onChange={(e) => set({ recvUserPhone: e.target.value })}
          />
        </div>
        <AutoAddHint show={genNew} />
      </div>

      {/* 우리 쪽 담당 두 칸(주 담당 · 함께 담당)은 한 칸에 위아래로 묶는다(10-21 way 「아래로 가는 것이 관리하기 쉽다」). */}
      <div className="grid content-start gap-4">
      {/* 수정 창에서 비우면 「나」가 아니라 **그대로**다(서버 update = coalesce) — 새 접수만 「나」. */}
      <Field label="버스인접수담당자" hint={d.recvId ? '비우면 지금 담당 그대로' : '비우면 내가 담당이 됩니다'}>
        <OwnerPicker
          value={d.ownerAppUserId}
          autoMe={!d.recvId}
          emptyLabel={d.recvId ? (d.legacyOwnerLabel ? `— 그대로 (${d.legacyOwnerLabel} · 버스인 기록)` : '— 미지정 (전원에게 보임)') : undefined}
          onPick={(id) => set({ ownerAppUserId: id })}
        />
      </Field>

      {/* 함께 담당(10-19 way) — 주 담당 옆에 우리 쪽 계정을 여럿. 함께 담당자도 이 접수를 자기 범위(본인·팀)에서 본다.
          칩 안에 버튼이 있어 <label>(Field) 로 감싸지 않는다(issues 103 — 바깥 label 이 클릭을 select 로 되쏜다). */}
      <div className="block">
        <span className="mb-1 block text-sm font-medium">
          함께 담당
          <span className="ml-2 text-xs font-normal text-ink-faint">버스인접수담당자 · 이 접수를 함께 봅니다</span>
        </span>
        <CoOwnerPicker
          value={d.coOwnerIds ?? []}
          labels={d.coOwnerLabels}
          exclude={d.ownerAppUserId}
          onChange={(ids) => set({ coOwnerIds: ids })}
        />
      </div>
      </div>

      <Field label="운행 시작일">
        <input
          type="date"
          className="field"
          value={d.recvStartdt ?? ''}
          onChange={(e) => set({ recvStartdt: e.target.value })}
        />
      </Field>

      <Field label="운행 종료일">
        <input
          type="date"
          className="field"
          value={d.recvEnddt ?? ''}
          onChange={(e) => set({ recvEnddt: e.target.value })}
        />
      </Field>

      <Field label="청구법인" required hint="비우면 이 거래처 지난 접수와 같게">
        <FinGrpPicker value={d.finGrpId} onPick={(id) => set({ finGrpId: id })} />
      </Field>

      {/* 청구 방법(#180) — 청구 요청(청구대상 이상)하기 전까지 바꿀 수 있다. 그 뒤엔 회계만(서버가 다시 막는다). */}
      <Field
        label="청구 방법"
        hint={d.finReqLocked ? `회계팀 청구 뒤라 바꿀 수 없습니다(${(d.billedMons ?? []).join(', ')}) — 회계팀에 요청` : '회계팀 청구 전까지 바꿀 수 있습니다'}
      >
        <select
          className="field disabled:bg-canvas disabled:text-ink-soft"
          value={d.finReqMethod ?? ''}
          disabled={d.finReqLocked === true}
          onChange={(e) => set({ finReqMethod: e.target.value })}
        >
          <option value="">선택 안 함</option>
          {REQ_METHODS.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
          {d.finReqMethod && !REQ_METHODS.includes(d.finReqMethod) && <option value={d.finReqMethod}>{d.finReqMethod}</option>}
        </select>
      </Field>

      <div className="block">
        <span className="mb-1 flex items-center gap-2 text-sm font-medium">
          <span className="whitespace-nowrap">거래처정산담당자</span>
          {finContacts.length > 0 && (
            <ContactChooser
              rows={finContacts}
              label={(x) => [x.dept ? `(${x.dept})` : '', x.name, x.phone].filter(Boolean).join(' · ')}
              onPick={(x) => set({ finManager: x.name, finPhone: x.phone })}
            />
          )}
        </span>
        <input
          className="field"
          aria-label="거래처정산담당자"
          value={d.finManager ?? ''}
          onChange={(e) => set({ finManager: e.target.value })}
        />
        <AutoAddHint show={finNew} />
      </div>

      <Field label="거래처정산담당자 연락처">
        <input className="field" value={d.finPhone ?? ''} onChange={(e) => set({ finPhone: e.target.value })} />
      </Field>

      <div className="md:col-span-2">
        <GrpMultiPicker
          ids={d.disptGrpIds}
          legacyText={d.disptGrp}
          onChange={(ids) => set({ disptGrpIds: ids })}
        />
      </div>

      {/* 「차량 지역」 칸은 뺐다(#272 윤일향 「필요 없을 듯」). 값(carArea)은 그대로 실려 저장된다 — 옛 값을 지우지 않는다. */}

      <div className="md:col-span-2">
        <Field label="접수 요청" hint="최대 50자">
          <input
            className="field"
            maxLength={50}
            value={d.recvRequest ?? ''}
            onChange={(e) => set({ recvRequest: e.target.value })}
          />
        </Field>
      </div>

      {/* 배차 입찰 공개/비공개(#179) — is_bidding 하나. 비공개 = 기사앱에 안 뜨고 사무실이 배차(노선 상태 「비공개입찰」). */}
      <div className="md:col-span-2">
        <span className="mb-1 block text-sm font-medium">배차 입찰</span>
        <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="배차 입찰">
          {[
            { v: false, t: '비공개', s: '기사앱에 안 보입니다 · 사무실에서 배차' },
            { v: true, t: '기사에게 공개', s: '기사앱 입찰 목록에 올라갑니다' },
          ].map((o) => (
            <label
              key={String(o.v)}
              className={`flex cursor-pointer select-none items-center gap-2 rounded-lg border px-3 py-2 text-sm ${
                (d.isBidding === true) === o.v ? 'border-brand-600 bg-brand-50 font-medium' : 'border-line'
              }`}
            >
              <input type="radio" name="recv-bidding" checked={(d.isBidding === true) === o.v} onChange={() => set({ isBidding: o.v })} />
              {o.t}
              <span className="text-xs font-normal text-ink-faint">{o.s}</span>
            </label>
          ))}
        </div>
      </div>

      {d.isBidding && (
        <>
          <Field label="입찰 유형">
            <select className="field" value={d.biddingType ?? ''} onChange={(e) => set({ biddingType: e.target.value })}>
              <option value="">선택 안 함</option>
              {BIDDING_TYPES.map((b) => (
                <option key={b} value={b}>
                  {b}
                </option>
              ))}
            </select>
          </Field>
          <Field label="입찰 마감일" required hint="입찰이면 필수 — 없으면 상태가 계산되지 않습니다">
            <input
              type="date"
              className="field"
              value={d.closeDate ?? ''}
              onChange={(e) => set({ closeDate: e.target.value })}
            />
          </Field>
        </>
      )}

      <div className="flex gap-5 md:col-span-2">
        <label className="flex cursor-pointer select-none items-center gap-2 text-sm">
          <input type="checkbox" checked={d.isMeal === true} onChange={(e) => set({ isMeal: e.target.checked })} />
          식사 포함
        </label>
        <label className="flex cursor-pointer select-none items-center gap-2 text-sm">
          <input type="checkbox" checked={d.isLodge === true} onChange={(e) => set({ isLodge: e.target.checked })} />
          숙박 포함
        </label>
      </div>
    </div>
  );
}

/** 칸 아래 안내 — 친 사람이 그 거래처 목록에 없을 때만. 저장하면 서버가 목록에 넣는다(10-30). */
function AutoAddHint({ show }: { show: boolean }) {
  if (!show) return null;
  return <div className="mt-1 text-xs text-ink-faint">새로 적은 분 — 접수를 저장하면 그 거래처의 담당자 목록에 자동으로 추가됩니다</div>;
}

/** 거래처에 담당자가 여럿이면 라벨 줄 끝에 「다른 담당자」 고르기(#177). */
function ContactChooser({
  rows,
  label,
  onPick,
}: {
  rows: CustContact[];
  label: (x: CustContact) => string;
  onPick: (x: CustContact) => void;
}) {
  return (
    <select
      className="ml-auto min-w-0 max-w-[14rem] truncate rounded border border-line bg-surface px-1 py-0.5 text-xs font-normal text-ink-soft"
      value=""
      aria-label="등록된 담당자에서 고르기"
      onChange={(e) => {
        const x = rows[Number(e.target.value)];
        if (x) onPick(x);
      }}
    >
      <option value="">등록된 담당자 ({rows.length})</option>
      {rows.map((x, i) => (
        <option key={`${x.id ?? 'b'}-${i}`} value={i}>{label(x)}</option>
      ))}
    </select>
  );
}

/**
 * 배차 소속 고르기(#179 윤일향) — 여러 개 · 「전체」 = 비움(제한 없음).
 * 저장은 id 배열(`disptGrpIds`) — 서버가 레거시처럼 이름 「, 」 이음(`dispt_grp`, 기사앱 입찰이 부분일치로 읽는다)과 id 이음을 같이 쓴다.
 * 레거시 접수(id 없이 이름만)는 이름으로 맞춰 체크만 보여 주고, 손대기 전엔 `disptGrpIds` 를 안 만든다(저장해도 그대로).
 */
function GrpMultiPicker({
  ids,
  legacyText,
  onChange,
}: {
  ids: number[] | undefined;
  legacyText: string | undefined;
  onChange: (ids: number[]) => void;
}) {
  const [grps, setGrps] = useState<{ grp_id: number; grp_name: string; grp_kind: string | null; hidden: boolean }[] | null>(null);
  useEffect(() => {
    let alive = true;
    api
      // 숨긴 소속(#389)까지 받아 두고, 이미 고른 것만 남긴다 — 옛 접수 소속이 숨겨져도 체크가 사라지지 않게.
      .listGrps({ limit: 200, scope: 'all' })
      .then((r) => {
        if (!alive) return;
        const rows = r.rows
          .filter((g) => g.grp_name)
          .map((g) => ({ grp_id: Number(g.grp_id), grp_name: g.grp_name as string, grp_kind: g.grp_kind, hidden: !!g.is_hidden }))
          // 내부 먼저, 외부 뒤 — 대부분 내부만 고른다.
          .sort((a, b) => Number(a.grp_kind === '외부') - Number(b.grp_kind === '외부') || a.grp_id - b.grp_id);
        setGrps(rows);
      })
      .catch(() => alive && setGrps([]));
    return () => { alive = false; };
  }, []);

  const legacy = (legacyText ?? '').split(',').map((s) => s.trim()).filter(Boolean);
  const selected = new Set<number>(
    ids ?? (grps ?? []).filter((g) => legacy.includes(g.grp_name)).map((g) => g.grp_id),
  );
  const all = ids ? ids.length === 0 : legacy.length === 0;
  const toggle = (id: number) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange([...next]);
  };

  return (
    <div>
      <span className="mb-1 block text-sm font-medium">
        배차 소속
        <span className="ml-2 text-xs font-normal text-ink-faint">여러 개 고를 수 있습니다 · 「전체」면 제한 없음 · 고른 소속 기사님에게만 입찰이 보입니다</span>
      </span>
      {grps === null ? (
        <div className="text-xs text-ink-faint">소속 불러오는 중…</div>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          <label className={`flex cursor-pointer select-none items-center gap-1.5 rounded-full border px-3 py-1 text-sm ${all ? 'border-brand-600 bg-brand-50 font-medium' : 'border-line'}`}>
            <input type="checkbox" checked={all} onChange={() => onChange([])} />
            전체
          </label>
          {grps.filter((g) => !g.hidden || selected.has(g.grp_id)).map((g) => (
            <label
              key={g.grp_id}
              className={`flex cursor-pointer select-none items-center gap-1.5 rounded-full border px-3 py-1 text-sm ${
                selected.has(g.grp_id) ? 'border-brand-600 bg-brand-50' : 'border-line'
              }`}
            >
              <input type="checkbox" checked={selected.has(g.grp_id)} onChange={() => toggle(g.grp_id)} />
              {g.grp_name}
              {g.grp_kind === '외부' && <span className="text-xs text-ink-faint">외부</span>}
              {g.hidden && <span className="text-xs text-ink-faint">숨김</span>}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

export function Field({
  label,
  required,
  hint,
  children,
}: {
  label: string;
  required?: boolean;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium">
        {label}
        {required && <span className="ml-0.5 text-red-600">*</span>}
        {hint && <span className="ml-2 text-xs font-normal text-ink-faint">{hint}</span>}
      </span>
      {children}
    </label>
  );
}

/**
 * 거래처 5,033건이라 셀렉트로 못 담는다. 검색해서 고른다.
 *
 * ⚠️ `<datalist>` 를 쓰지 않는다 — 값이 채워지면 목록이 가려져 다시 고를 수 없게 되는 함정이 있다.
 */
/** 거래처 고르기 줄의 담당자(#256) — 같은 이름 거래처가 많아(국민대 40여 곳) 담당자로 가린다. 앞 3명 + 외 N명. */
function contactsOf(c: Cust): string {
  const names = [
    ...(c.contact_names ?? '').split(', '),
    c.manager ?? '',
    (c as { cust_fin_manager?: string | null }).cust_fin_manager ?? '',
  ].map((x) => x.trim()).filter(Boolean);
  const uniq = [...new Set(names)];
  return uniq.length > 3 ? `${uniq.slice(0, 3).join(', ')} 외 ${uniq.length - 3}명` : uniq.join(', ');
}

export function CustPicker({
  value,
  label,
  onPick,
  onTerm,
}: {
  value?: number;
  label?: string;
  onPick: (c: Cust) => void;
  /** 친 글자를 바깥에 알린다 — 새 접수는 고르지 않으면 이 이름으로 새 거래처를 만든다(#498). */
  onTerm?: (term: string) => void;
}) {
  const [term, setTermRaw] = useState('');
  const setTerm = (v: string) => { setTermRaw(v); onTerm?.(v); };
  const [hits, setHits] = useState<Cust[]>([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  // 찾는 거래처가 없으면 여기서 바로 새로 등록한다(10-20 #354 — 배차 입력 권한이면 새 거래처 등록 가능).
  const { me } = useMe();
  const canCreate = canScreen(me, ['master', 'dispatch']);
  const [creating, setCreating] = useState(false);

  /** 실제 조회. 버튼·Enter 와 자동검색이 같은 경로를 쓴다. */
  async function run(raw: string) {
    const q = raw.trim();
    if (q === '') return;
    setBusy(true);
    try {
      const r = await api.listCusts({ q, limit: 50 }); // #256 — 「국민대」만 40여 곳이라 20개면 뒤가 잘린다
      setHits(r.rows);
      setOpen(true);
    } finally {
      setBusy(false);
    }
  }

  function search(e: React.FormEvent) {
    e.preventDefault();
    void run(term);
  }

  // 치는 대로 찾는다(입력이 멎으면 적용). 버튼·Enter 는 그대로 — `lib/search.ts` 참고.
  useAutoSearch(term, (v) => { void run(v); });

  if (value && !open) {
    return (
      <div className="flex items-center gap-2">
        <div className="field flex-1 bg-canvas">{label || `거래처 ${value}`}</div>
        <button type="button" className="btn-ghost" onClick={() => setOpen(true)}>
          변경
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <input
          className="field flex-1"
          placeholder="거래처명 또는 사업자번호"
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          onKeyDown={(e) => {
            // 폼 안에 중첩돼 있으므로 Enter 가 바깥 저장으로 새지 않게 여기서 삼킨다.
            if (e.key === 'Enter') {
              e.preventDefault();
              void search(e as unknown as React.FormEvent);
            }
          }}
        />
        <button type="button" className="btn-ghost" disabled={busy} onClick={(e) => void search(e)}>
          찾기
        </button>
      </div>
      {hits.length > 0 && (
        <ul className="max-h-48 overflow-y-auto rounded-lg border border-line">
          {hits.map((c) => (
            <li key={c.cust_id}>
              <button
                type="button"
                className="w-full px-3 py-2 text-left text-sm hover:bg-canvas"
                onClick={() => {
                  onPick(c);
                  setOpen(false);
                  setHits([]);
                  setTerm('');
                }}
              >
                {c.cust_name}
                {c.biz_number && <span className="ml-2 text-xs text-ink-faint">{c.biz_number}</span>}
                {c.phone?.trim() && <span className="ml-2 text-xs text-ink-faint">☎ {c.phone.trim()}</span>}
                {contactsOf(c) && <span className="ml-2 text-xs text-ink-soft">거래처담당자 {contactsOf(c)}</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
      {open && hits.length === 0 && !busy && <p className="text-xs text-ink-faint">검색어를 넣고 “찾기”를 누르세요.</p>}
      {canCreate && (
        <button type="button" className="text-xs text-brand-700 underline" onClick={() => setCreating(true)}>
          + 새 거래처 등록
        </button>
      )}
      {creating && (
        <Modal width="max-w-4xl" draggable>
          <MasterForm
            kind="cust"
            row={null}
            onClose={() => setCreating(false)}
            onSaved={() => setCreating(false)}
            onCreated={(id, name) => {
              onPick({ cust_id: id, cust_name: name } as Cust);
              setOpen(false);
              setHits([]);
              setTerm('');
            }}
          />
        </Modal>
      )}
    </div>
  );
}

/**
 * 새 접수 — 거래처를 찾아 고르지 않고 바로 치기(11-04 #498 윤일향 「찾기 없이도 입력하면 진행」).
 * 거래처 칸에 친 이름 + 여기 사업자등록번호로 저장하면 서버가 같은 번호 거래처를 쓰거나 접수와 함께 새로 만든다
 * (`recvIntake.ts` · `masterWrite.custForRecvTx`). 여기서는 같은 번호·이름 거래처를 미리 알려 주고 그걸로 고를 수 있게만 한다.
 */
function NewCustBiz({ name, biz, onBiz, onPick }: {
  name: string;
  biz: string;
  onBiz: (v: string) => void;
  onPick: (c: Cust) => void;
}) {
  const [hit, setHit] = useState<{ byBiz: { cust_id: number; cust_name: string | null }[]; byName: { cust_id: number; cust_name: string | null }[] } | null>(null);
  const digits = biz.replace(/\D/g, '');
  const nm = name.trim();
  useEffect(() => {
    if (digits.length < 10 && !nm) { setHit(null); return; }
    let alive = true;
    const t = setTimeout(() => {
      api.checkCustDup({ bizNumber: digits.length >= 10 ? digits : '', custName: nm })
        .then((r) => { if (alive) setHit(r); })
        .catch(() => { if (alive) setHit(null); });
    }, 400);
    return () => { alive = false; clearTimeout(t); };
  }, [digits, nm]);
  const same = hit?.byBiz[0];
  const sameName = !same ? hit?.byName[0] : undefined;
  return (
    <div className="mt-2 space-y-1">
      <input
        className="field"
        aria-label="사업자등록번호"
        placeholder="사업자등록번호"
        inputMode="numeric"
        value={biz}
        onChange={(e) => onBiz(e.target.value)}
      />
      {same ? (
        <p className="text-xs text-ink-soft">
          이미 등록된 거래처 「{same.cust_name}」입니다 — 저장하면 이 거래처로 접수됩니다.{' '}
          <button type="button" className="text-brand-700 underline" onClick={() => onPick({ cust_id: same.cust_id, cust_name: same.cust_name } as Cust)}>
            이 거래처로 고르기
          </button>
        </p>
      ) : sameName ? (
        <p className="text-xs text-amber-700">
          같은 이름의 거래처 「{sameName.cust_name}」가 있습니다 — 같은 곳이면 위 칸에서 찾아 고르세요.
        </p>
      ) : (
        <p className="text-xs text-ink-faint">목록에서 고르지 않으면 거래처 칸에 친 이름과 이 사업자등록번호로 새 거래처가 함께 등록됩니다.</p>
      )}
    </div>
  );
}

/**
 * 접수담당자 선택기.
 *
 * 거래처처럼 검색으로 찾지 않는다 — 사내 계정은 많아야 수십 명이라 드롭다운 하나가 더 빠르다.
 * 목록은 세션당 한 번만 부른다(화면을 열 때마다 왕복을 늘리지 않는다).
 *
 * **비워 두면 등록한 사람이 담당**이 된다 — 그게 대부분의 경우다. 서버가 그렇게 채운다.
 * 새 접수(`autoMe`)는 처음부터 내 이름을 골라 둔다(11-03 way — 「— 나」만 보여 저장 안 되는 줄 알았다 · 저장은 82/82 정상이었다).
 * 한 번만 채운다 — 사람이 「— 나」나 다른 사람으로 바꾸면 그대로 둔다.
 */
function OwnerPicker({ value, emptyLabel, autoMe, onPick }: { value?: number; emptyLabel?: string; autoMe?: boolean; onPick: (id: number | undefined) => void }) {
  const [staff, setStaff] = useState<Staff[]>([]);
  const [error, setError] = useState(false);
  const { me } = useMe();
  const filled = useRef(false);

  useEffect(() => {
    let cancelled = false;
    loadStaff()
      .then((r) => { if (!cancelled) setStaff(r.rows); })
      .catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!autoMe || filled.current || value !== undefined || !me?.email || !staff.length) return;
    const mine = staff.find((s) => s.email?.toLowerCase() === me.email.toLowerCase());
    filled.current = true;
    if (mine) onPick(mine.app_user_id);
  }, [autoMe, value, me?.email, staff, onPick]);

  // 목록을 못 불러왔다고 저장을 막지 않는다 — 비워 두면 등록한 사람이 담당이 되므로 안전하다.
  if (error) return <p className="text-xs text-ink-faint">버스인접수담당자 목록을 불러오지 못했습니다. 비워 두면 내가 버스인접수담당자가 됩니다.</p>;

  return (
    <select
      className="field"
      value={value ?? ''}
      onChange={(e) => onPick(e.target.value === '' ? undefined : Number(e.target.value))}
    >
      <option value="">{emptyLabel ?? '— 나 (등록한 사람)'}</option>
      {staff.map((s) => (
        <option key={s.app_user_id} value={s.app_user_id}>
          {s.name || s.email}
          {s.team ? ` · ${s.team}` : ''}
        </option>
      ))}
    </select>
  );
}

/**
 * 함께 담당 고르기(10-19) — 칩 + 사람 고르기. 목록은 주 담당과 같은 소스(`loadStaff` = 활성 계정 · 세션당 한 번).
 * 주 담당으로 고른 사람은 고르기에서 뺀다(서버도 뺀다). 비활성이 돼 목록에 없는 사람은 칩으로만 남는다(× 로 뺄 수 있다).
 */
function CoOwnerPicker({ value, labels, exclude, onChange }: {
  value: number[];
  labels?: Record<string, string>;
  exclude?: number;
  onChange: (ids: number[]) => void;
}) {
  const [staff, setStaff] = useState<Staff[]>([]);
  const [error, setError] = useState(false);
  useEffect(() => {
    let cancelled = false;
    loadStaff()
      .then((r) => { if (!cancelled) setStaff(r.rows); })
      .catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, []);

  const nameOf = (id: number) => {
    const s = staff.find((x) => Number(x.app_user_id) === id);
    return s ? (s.name || s.email) : (labels?.[String(id)] ?? `계정 ${id}`);
  };
  const options = staff.filter((s) => !value.includes(Number(s.app_user_id)) && Number(s.app_user_id) !== exclude);

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {value.map((id) => (
        <span key={id} className="inline-flex items-center gap-1 rounded-full bg-brand-50 px-2.5 py-0.5 text-sm">
          {nameOf(id)}
          <button
            type="button"
            className="leading-none text-ink-faint hover:text-red-600"
            aria-label={`${nameOf(id)} 함께 담당에서 빼기`}
            title="빼기"
            onClick={() => onChange(value.filter((x) => x !== id))}
          >
            ×
          </button>
        </span>
      ))}
      {error ? (
        <span className="text-xs text-ink-faint">버스인접수담당자 목록을 불러오지 못했습니다.</span>
      ) : (
        <select
          className="field w-auto"
          aria-label="함께 담당 추가"
          value=""
          onChange={(e) => {
            const id = Number(e.target.value);
            if (id > 0 && !value.includes(id)) onChange([...value, id]);
          }}
        >
          <option value="">+ 함께 담당 추가</option>
          {options.map((s) => (
            <option key={s.app_user_id} value={s.app_user_id}>
              {s.name || s.email}
              {s.team ? ` · ${s.team}` : ''}
            </option>
          ))}
        </select>
      )}
      {value.length === 0 && !error && <span className="text-xs text-ink-faint">없음</span>}
    </div>
  );
}

/**
 * 청구법인 선택기(#138) — 계산서 발행·거래처별 정산 거르기가 이 값을 쓴다.
 * 비워 두면 서버가 **이 거래처의 지난 접수 법인**을 넣는다(처음 거래처면 고르라고 막는다).
 * 목록에 없는 옛 값(회계 법인에 안 이어진 소속)도 지워지지 않게 그대로 한 줄 보여 준다.
 */
export function FinGrpPicker({ value, onPick }: { value?: number; onPick: (id: number | undefined) => void }) {
  const [rows, setRows] = useState<{ grp_id: number; grp_name: string }[]>([]);
  useEffect(() => {
    let cancelled = false;
    api.listRecvFinGrps({}).then((r) => { if (!cancelled) setRows(r.rows); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);
  const known = value === undefined || rows.some((r) => Number(r.grp_id) === value);
  return (
    <select
      className="field"
      value={value ?? ''}
      onChange={(e) => onPick(e.target.value === '' ? undefined : Number(e.target.value))}
    >
      <option value="">— 이 거래처 지난 접수와 같게</option>
      {rows.map((r) => (
        <option key={r.grp_id} value={r.grp_id}>{r.grp_name}</option>
      ))}
      {!known && <option value={value}>소속 {value}</option>}
    </select>
  );
}

/**
 * 거래처 대표 전화 — 그 자리에서 고치기(10-25 #400 윤일향).
 * 기준정보 또는 배차 입력 권한이면 「고치기」(서버 saveCustPhone · 거래처 기준정보 전화가 바뀐다).
 */
function CustTelLine({ custId, phone, onSaved }: { custId: number; phone: string; onSaved: (p: string) => void }) {
  const { me } = useMe();
  const canEdit = canScreen(me, ['master', 'dispatch']);
  const [edit, setEdit] = useState(false);
  const [val, setVal] = useState(phone);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  async function save() {
    setBusy(true); setErr(null);
    try {
      const r = await api.saveCustPhone({ custId, phone: val.trim() });
      onSaved(r.phone);
      setEdit(false);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }
  if (edit) {
    return (
      <div className="mt-1 space-y-1">
        <div className="flex items-center gap-1 text-xs">
          <span className="text-ink-soft">거래처 전화</span>
          <input
            className="field h-7 max-w-[11rem] py-0 text-xs"
            value={val}
            autoFocus
            onChange={(e) => setVal(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void save(); } if (e.key === 'Escape') setEdit(false); }}
          />
          <button type="button" className="btn-primary py-0.5 text-xs" disabled={busy || !val.trim()} onClick={() => void save()}>
            {busy ? '저장 중…' : '저장'}
          </button>
          <button type="button" className="btn-ghost py-0.5 text-xs" disabled={busy} onClick={() => { setEdit(false); setErr(null); }}>
            취소
          </button>
        </div>
        {err && <p className="text-xs text-red-600">{err}</p>}
      </div>
    );
  }
  return (
    <p className="mt-1 text-xs text-ink-soft">
      거래처 전화 <span className="font-medium text-ink">{phone || '등록 안 됨'}</span>
      {canEdit && (
        <button type="button" className="ml-2 text-brand-700 underline" onClick={() => { setVal(phone); setEdit(true); }}>
          고치기
        </button>
      )}
    </p>
  );
}
